/**
 * L'import par fichier CSV — ce que l'écran calcule, hors React.
 *
 * Même découpe que partout : ce fichier ne dessine rien et n'appelle rien. Il
 * dit ce qu'on affiche, et il s'éprouve sans navigateur.
 *
 * LES SIX SORTES SONT ÉCRITES ICI ET NON DÉDUITES d'un descripteur, contrairement
 * au sous-menu de l'annuaire. La raison : ce que l'écran doit dire d'un fichier
 * — ce qui fait un doublon, ce qui est obligatoire, ce qui manquera après —
 * n'est écrit dans aucun descripteur existant. Le déduire à moitié et compléter
 * à la main ferait deux sources pour une même liste.
 */

import type { RapportImport, SorteImport } from './api.js';

export interface SorteDite {
  readonly cle: SorteImport;
  /** L'entrée de la liste : « Clients ». */
  readonly nom: string;
  /** Le mot du décompte, au singulier : « client ». */
  readonly singulier: string;
  /**
   * CE MOT EST-IL FÉMININ ? Une remorque l'est, les cinq autres non.
   *
   * Sans ce drapeau, l'écran écrit « 4 remorques importés ». Le même défaut a
   * déjà été trouvé le 03/09/2026 sur l'écran du parc — « Aucun remorque » — et
   * il revient dès qu'on accorde un participe à la main.
   */
  readonly feminin: boolean;
  /** Une phrase, jamais deux. */
  readonly aide: string;
  /**
   * CE QUI FAIT LE DOUBLON, dit en français, ou nul quand la base n'en connaît
   * pas. Ce n'est pas décoratif : sans cette phrase, un utilisateur qui importe
   * deux fois le même chauffeur ne comprendrait pas pourquoi Stafa a laissé
   * passer ce qu'il refuse ailleurs.
   */
  readonly doublon: string | null;
}

export const SORTES_IMPORT: readonly SorteDite[] = [
  {
    cle: 'clients',
    nom: 'Clients',
    singulier: 'client',
    feminin: false,
    aide: 'Seul le nom est obligatoire. Le code sert à retrouver la fiche et doit être unique.',
    doublon: 'un code déjà employé',
  },
  {
    cle: 'sites',
    nom: 'Sites',
    singulier: 'site',
    feminin: false,
    aide: 'Un site est un LIEU : il n’a ni SIRET, ni conditions de paiement, et n’est rattaché à aucun client.',
    doublon: 'un code déjà employé',
  },
  {
    cle: 'transporteurs',
    nom: 'Transporteurs',
    singulier: 'transporteur',
    feminin: false,
    aide: 'Le matériel et les zones desservies ne s’importent pas encore : ils se cochent sur la fiche.',
    doublon: 'un code déjà employé',
  },
  {
    cle: 'vehicules',
    nom: 'Véhicules',
    singulier: 'véhicule',
    feminin: false,
    aide: 'Le type d’un camion doit exister dans l’onglet « Types de véhicule » avant l’import.',
    doublon: 'une immatriculation déjà employée, sans égard à la casse',
  },
  {
    cle: 'remorques',
    nom: 'Remorques',
    singulier: 'remorque',
    feminin: true,
    aide: 'Le gabarit est un code du référentiel commun — « T6 » pour une semi tautliner.',
    doublon: 'une immatriculation déjà employée, sans égard à la casse',
  },
  {
    cle: 'chauffeurs',
    nom: 'Chauffeurs',
    singulier: 'chauffeur',
    feminin: false,
    // Vérifié dans `20260903210000_parc_et_chauffeurs.sql` : la table n'a qu'un
    // index de recherche sur le nom, pas d'index unique.
    aide: 'Un chauffeur n’a que son nom : c’est tout ce que la base porte.',
    doublon: null,
  },
];

export function sorteDite(cle: SorteImport): SorteDite {
  return SORTES_IMPORT.find((s) => s.cle === cle) ?? (SORTES_IMPORT[0] as SorteDite);
}

/** Le nom du fichier modèle, tel que l'API le propose. */
export function nomModele(cle: SorteImport): string {
  return `stafa-${cle}.csv`;
}

/**
 * L'IMPORT EST-IL POSSIBLE ?
 *
 * Trois conditions, et le bouton ne s'allume qu'aux trois. C'est le tout ou
 * rien rendu visible : tant qu'il reste une faute, il n'y a rien à tenter.
 */
export function peutImporter(rapport: RapportImport): boolean {
  return rapport.refus.length === 0 && rapport.total > 0 && rapport.fautes.length === 0;
}

/**
 * CE QUE L'ANALYSE A TROUVÉ, en une phrase.
 *
 * Elle dit toujours COMBIEN, parce que c'est le premier contrôle que fait un
 * humain : « j'en attendais 260 ». Un fichier tronqué par un enregistrement
 * raté ne se voit qu'à ce nombre.
 */
export function resumeAnalyse(rapport: RapportImport): string {
  if (rapport.refus.length > 0) {
    return 'Le fichier n’a pas pu être lu.';
  }
  if (rapport.total === 0) {
    return 'Le fichier ne contient aucune ligne de données.';
  }

  const pluriel = rapport.total > 1;
  const lignes = `${String(rapport.total)} ligne${pluriel ? 's' : ''}`;
  if (rapport.fautes.length === 0) {
    // « prête » s'accorde avec « ligne » : une seule ligne est PRÊTE, pas
    // « prêtes ». La faute était dans la première version, et le test la
    // figeait — corrigé le 07/09/2026.
    return `${lignes}, ${pluriel ? 'prêtes' : 'prête'} à importer.`;
  }

  const combien = rapport.fautes.length;
  return (
    `${lignes}, dont ${String(combien)} à corriger. ` +
    'Rien ne sera importé tant qu’il en reste une.'
  );
}

/**
 * LES PREMIERS NOMS LUS, pour reconnaître le fichier d'un coup d'œil.
 *
 * C'est ce qui attrape l'erreur qu'aucun contrôle ne peut voir : avoir déposé
 * le fichier des sites dans l'onglet des clients. Les colonnes sont presque les
 * mêmes, tout passerait — seuls les noms le disent.
 */
export function apercuDit(rapport: RapportImport): string | null {
  if (rapport.apercu.length === 0) return null;
  const reste = rapport.total - rapport.apercu.length;
  const debut = rapport.apercu.join(', ');
  if (reste <= 0) return debut;
  return `${debut}… et ${String(reste)} autre${reste > 1 ? 's' : ''}`;
}

/**
 * Ce qu'on écrit après un import réussi.
 *
 * LE PARTICIPE S'ACCORDE EN GENRE ET EN NOMBRE. « 4 remorques importés » était
 * la première version, et le test la figeait — même famille de faute que
 * « Aucun remorque » du 03/09/2026.
 */
export function importDit(sorte: SorteDite, combien: number): string {
  const pluriel = combien > 1;
  const nom = `${sorte.singulier}${pluriel ? 's' : ''}`;
  const participe = `importé${sorte.feminin ? 'e' : ''}${pluriel ? 's' : ''}`;
  return `${String(combien)} ${nom} ${participe}.`;
}
